import { useEffect, useState } from "react";
import { ArrowUpRight, Globe } from "lucide-react";
import Reveal from "./Reveal";
import { api } from "@/lib/api";

const FALLBACK = [
  {
    key: "booking",
    domain: "booking.tourism-connect.com",
    desc: "Real-time booking engine for lodges, safaris and experiences.",
    status: "live",
    href: "https://booking.tourism-connect.com",
  },
  {
    key: "hotels",
    domain: "hotels.tourism-connect.com",
    desc: "Direct hotel & lodge distribution without the OTA tax.",
    status: "soon",
  },
  {
    key: "tours",
    domain: "tours.tourism-connect.com",
    desc: "Curated multi-day tours, packaged and bookable end-to-end.",
    status: "soon",
  },
  {
    key: "partners",
    domain: "partners.tourism-connect.com",
    desc: "Trade portal for rates, allocations and co-marketing.",
    status: "soon",
  },
];

export default function Subdomains() {
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    api
      .get("/subdomains")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setItems(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="subdomains" className="border-t border-goldline bg-coal/40">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-28">
        <Reveal>
          <p className="kicker mb-6">The Network</p>
          <h2 className="max-w-2xl font-serif text-3xl font-semibold leading-snug tracking-tight sm:text-4xl lg:text-5xl">
            One brand. <span className="text-gold">Many doors.</span>
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted">
            Each subdomain is a purpose-built product on the same connected inventory.
            Booking is live today — the rest of the family is boarding soon.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((s, i) => {
            const inner = (
              <>
                <div className="flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 bg-gold/10 text-gold">
                    <Globe size={16} />
                  </span>
                  {s.status === "live" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-green-500">
                      <span className="live-dot h-1.5 w-1.5 rounded-full bg-green-500" />
                      Live
                    </span>
                  ) : (
                    <span className="rounded-full border border-gold/25 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold/60">
                      Coming soon
                    </span>
                  )}
                </div>
                <p className="mt-6 break-all font-mono text-sm leading-relaxed text-cream">{s.domain}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted">{s.desc}</p>
                {s.status === "live" && (
                  <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                    Visit now <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                )}
              </>
            );
            const cls = `group block h-full rounded-2xl border p-7 transition-[border-color,transform,box-shadow] duration-500 ${
              s.status === "live"
                ? "border-gold/30 bg-panel/80 hover:-translate-y-1 hover:border-gold/60 hover:shadow-[0_24px_60px_-24px_rgba(158,122,34,0.3)]"
                : "border-line bg-panel/40"
            }`;
            return (
              <Reveal key={s.key} delay={0.07 * i}>
                {s.status === "live" && s.href ? (
                  <a data-testid={`subdomain-card-${s.key}`} href={s.href} target="_blank" rel="noopener noreferrer" className={cls}>
                    {inner}
                  </a>
                ) : (
                  <div data-testid={`subdomain-card-${s.key}`} className={cls}>
                    {inner}
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
